/**
 * Hook de estado y lógica de negocio para el módulo de Contratos.
 * Extraído de AdminPanel (App.js) sin cambios de comportamiento.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  emptyContractForm,
  contractToForm,
  sortByFolioDesc,
  filterBySearchTerm,
} from "./utils";
import {
  fetchContracts as apiFetchContracts,
  createContract as apiCreateContract,
  updateContract as apiUpdateContract,
  deleteContract as apiDeleteContract,
  cleanupAndAssignFolios as apiCleanupAndAssignFolios,
  toggleContractCancelled as apiToggleContractCancelled,
  addAbono as apiAddAbono,
  updateAbono as apiUpdateAbono,
  deleteAbono as apiDeleteAbono,
} from "./api";
import { printContractPDF as printContractPDFModule } from "./pdf/contractPDF";
import { printProveedorPDF as printProveedorPDFModule } from "./pdf/proveedorPDF";
import { generateRecibo as generateReciboModule, generateAbonoRecibo as generateAbonoReciboModule } from "./pdf/reciboPDF";

export function useContracts(fetchData) {
  const [contracts, setContracts] = useState([]);
  const [contractsSearch, setContractsSearch] = useState("");
  const [showContractForm, setShowContractForm] = useState(false);
  const [editingContractId, setEditingContractId] = useState(null);
  const [contractPreview, setContractPreview] = useState(null);
  const [contractForm, setContractForm] = useState({ ...emptyContractForm });

  const createContract = async () => {
    if (!contractForm.client_name || !contractForm.client_phone || !contractForm.event_date) {
      toast.error("Complete: Nombre, Teléfono y Fecha");
      return;
    }
    try {
      await apiCreateContract(contractForm);
      toast.success("Contrato creado exitosamente");
      setShowContractForm(false);
      setContractForm({ ...emptyContractForm });
      setContractPreview(null);
      fetchData();
    } catch (e) {
      toast.error("Error al crear contrato");
    }
  };

  const deleteContract = async (id) => {
    if (!confirm("¿Eliminar contrato?")) return;
    await apiDeleteContract(id);
    toast.success("Contrato eliminado");
    fetchData();
  };

  const startEditContract = (contract) => {
    setEditingContractId(contract.id);
    setContractForm(contractToForm(contract));
    setShowContractForm(true);
    toast.info(`Editando contrato: ${contract.event_name}`);
  };

  const updateContract = async () => {
    if (!editingContractId) return;
    try {
      await apiUpdateContract(editingContractId, contractForm);
      toast.success("Contrato actualizado exitosamente");
      setShowContractForm(false);
      setEditingContractId(null);
      setContractForm({ ...emptyContractForm });
      setContractPreview(null);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar contrato");
    }
  };

  const cancelEdit = () => {
    setEditingContractId(null);
    setShowContractForm(false);
    setContractForm({ ...emptyContractForm });
  };

  // Callback que propaga el folio asignado por el backend al estado local
  const onContractFolioAssigned = (contractId, folio) => {
    setContracts((prev) => prev.map((c) => (c.id === contractId ? { ...c, folio } : c)));
  };

  const printContractPDF = (contract) =>
    printContractPDFModule(contract, { onFolioAssigned: onContractFolioAssigned });
  const printProveedorPDF = (contract) => printProveedorPDFModule(contract);
  const generateRecibo = (contract) => generateReciboModule(contract);

  const syncFolios = async () => {
    if (!window.confirm("Esto borrará contratos de PRUEBA y asignará folios reales (PIC-348..PIC-357). ¿Continuar?")) return;
    try {
      const res = await apiCleanupAndAssignFolios();
      const { deleted_count, assigned, not_found } = res.data;
      const contractsRes = await apiFetchContracts();
      setContracts(contractsRes.data);
      toast.success(`Limpieza completada: ${deleted_count} eliminados, ${assigned.length} folios asignados, ${not_found.length} no encontrados`);
    } catch (e) {
      toast.error("Error en la sincronización de folios");
    }
  };

  const filteredSortedContracts = (term) => filterBySearchTerm(contracts, term).sort(sortByFolioDesc);

  // ============ REPORTE DE PAGOS (abonos) ============

  const addAbono = async (contractId, abonoData) => {
    try {
      await apiAddAbono(contractId, abonoData);
      toast.success("Pago registrado");
      fetchData();
      return true;
    } catch (e) {
      toast.error("Error al registrar el pago");
      return false;
    }
  };

  const editAbono = async (contractId, abonoId, abonoData) => {
    try {
      await apiUpdateAbono(contractId, abonoId, abonoData);
      toast.success("Pago actualizado");
      fetchData();
      return true;
    } catch (e) {
      toast.error("Error al actualizar el pago");
      return false;
    }
  };

  const removeAbono = async (contractId, abonoId) => {
    if (!window.confirm("¿Eliminar este pago? Esta acción no se puede deshacer.")) return;
    try {
      await apiDeleteAbono(contractId, abonoId);
      toast.success("Pago eliminado");
      fetchData();
    } catch (e) {
      toast.error("Error al eliminar el pago");
    }
  };

  const toggleContractCancelled = async (contract) => {
    const confirmMsg = contract.status === "cancelled"
      ? "¿Reactivar este contrato?"
      : "¿Marcar este contrato como Cancelado? Dejará de contarse como saldo pendiente en el reporte de pagos.";
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await apiToggleContractCancelled(contract.id);
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar el estado");
    }
  };

  const generateAbonoRecibo = (contract, abono, saldoRestante) =>
    generateAbonoReciboModule(contract, abono, saldoRestante);

  return {
    contracts, setContracts,
    contractsSearch, setContractsSearch,
    showContractForm, setShowContractForm,
    editingContractId,
    contractForm, setContractForm,
    contractPreview,
    createContract, updateContract, deleteContract,
    startEditContract, cancelEdit,
    printContractPDF, printProveedorPDF, generateRecibo,
    syncFolios,
    filteredSortedContracts,
    addAbono, editAbono, removeAbono, toggleContractCancelled, generateAbonoRecibo,
  };
}
